"use client";

// ─────────────────────────────────────────────────────────────────────────
// "Did we actually leave for Stripe?" — the marker that arms the domain-flow
// restore (PANDA-1383 follow-up).
//
// A free user who clicks "Upgrade to buy" next to a found domain gets sent to
// Stripe Checkout. The project page already stashes their search results + pick
// in localStorage ("panda:reopen-domain-after-upgrade") and already knows how to
// restore them on the way back — on ?upgraded=true (paid) OR, when the user
// cancels / hits browser back, on the strength of THIS marker.
//
// The marker is what distinguishes a genuine return-from-Stripe from a plain
// revisit to the project that merely happens to have a stale localStorage entry
// lying around. Without it the domain modal would auto-pop on unrelated visits.
//
// It has to be set by whoever performs the actual navigation to Stripe. That
// used to be UpgradeModal/MobileUpgradeModal, which are now retired — the live
// path is RatedCreditsModal → rated-credits-flow.checkout(). Nothing on that
// path set the marker, so the cancel/back restore could never fire and the user
// came back to an empty publish modal with their domain search gone. Hence this
// module: one owner for the key, imported by the setter (rated-credits-flow) and
// the reader (app/project/[id]/page.tsx), so the two can't drift.
//
// sessionStorage, not localStorage: the signal is scoped to this tab and this
// browsing session, exactly like the round-trip it describes.
// ─────────────────────────────────────────────────────────────────────────

const REDIRECT_KEY = "panda:domain-upgrade-redirect";

/** True when this checkout was started from the domain flow. */
export function isDomainUpgradeIntent(intent: string | undefined): boolean {
  return typeof intent === "string" && intent.startsWith("domain:");
}

/** Call immediately before navigating to hosted Stripe Checkout. */
export function markDomainUpgradeRedirect(): void {
  try {
    sessionStorage.setItem(REDIRECT_KEY, "1");
  } catch {}
}

/**
 * Read AND clear the marker. One-shot by design: a return is consumed once, so
 * a later visit in the same tab can't be mistaken for another cancel-return.
 */
export function consumeDomainUpgradeRedirect(): boolean {
  let redirected = false;
  try {
    redirected = sessionStorage.getItem(REDIRECT_KEY) === "1";
    if (redirected) sessionStorage.removeItem(REDIRECT_KEY);
  } catch {
    return false;
  }
  return redirected;
}

/** Peek without consuming — for deciding whether a bfcache restore is ours. */
export function hasDomainUpgradeRedirect(): boolean {
  try {
    return sessionStorage.getItem(REDIRECT_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Drop the marker without acting on it — used by the ?upgraded=true success
 * path, which owns the return itself and must leave nothing behind that the
 * cancel path could later pick up.
 */
export function clearDomainUpgradeRedirect(): void {
  try {
    sessionStorage.removeItem(REDIRECT_KEY);
  } catch {}
}
